import React, { useState, useEffect } from 'react';
import { HeartIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface BuckPerformance {
  buck_tag: string;
  total_breedings: number;
  successful_breedings: number;
  success_rate: number;
  average_gestation_days?: number;
  kids_produced: number;
}

interface DoePerformance {
  doe_tag: string;
  total_breedings: number;
  successful_breedings: number;
  success_rate: number;
  average_kidding_interval_days?: number;
  total_kids_born: number;
  average_kids_per_kidding?: number;
}

interface PregnancyStatus {
  doe_tag: string;
  buck_tag: string;
  breeding_date: string;
  pregnancy_check: boolean;
  expected_kidding?: string;
  days_since_breeding: number;
  days_until_kidding?: number;
  status: string;
}

interface BreedingPerformanceSummary {
  total_breedings: number;
  confirmed_pregnancies: number;
  pregnancy_rate: number;
  average_gestation_days: number;
  breeding_to_kidding_success_rate: number;
  total_kids_from_breedings: number;
  average_kids_per_breeding: number;
  active_pregnancies: number;
  upcoming_kiddings_30_days: number;
}

interface GoatBreedingPerformanceReport {
  location_id: string;
  location_name: string;
  report_date: string;
  period_start?: string;
  period_end?: string;
  summary: BreedingPerformanceSummary;
  buck_performance: BuckPerformance[];
  doe_performance: DoePerformance[];
  current_pregnancies: PregnancyStatus[];
}

const GoatBreedingPerformanceReportPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [report, setReport] = useState<GoatBreedingPerformanceReport | null>(null);

  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const januaryFirst = new Date(`${currentYear}-01-01T00:00:00+05:30`);
    
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEndDate(formatDate(today));
    setStartDate(formatDate(januaryFirst));
  }, []);

  useEffect(() => {
    if (selectedLocation?.id && startDate && endDate) {
      fetchReport();
    }
  }, [selectedLocation?.id, startDate, endDate]);

  const fetchReport = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/goat/report/breeding-performance', {
        params: {
          location_id: selectedLocation.id,
          start: startDate,
          end: endDate,
        },
      });
      setReport(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load breeding performance report');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN');
    } catch {
      return dateStr;
    }
  };

  const getStatusColor = (status: string) => {
    if (status === 'overdue') return 'text-red-600';
    if (status === 'pregnant') return 'text-green-600';
    return 'text-gray-600';
  };

  if (loading) {
    return <div className="container-mobile py-6 text-center">Loading...</div>;
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="text-center">
        <HeartIcon className="h-16 w-16 text-pink-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Breeding Performance & Pregnancy Tracking</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-gray-900">{report.summary.total_breedings}</div>
              <div className="text-sm text-gray-600">Total Breedings</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-green-600">{report.summary.pregnancy_rate.toFixed(1)}%</div>
              <div className="text-sm text-gray-600">Pregnancy Rate</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-blue-600">{report.summary.active_pregnancies}</div>
              <div className="text-sm text-gray-600">Active Pregnancies</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="text-2xl font-bold text-purple-600">{report.summary.upcoming_kiddings_30_days}</div>
              <div className="text-sm text-gray-600">Upcoming (30 days)</div>
            </div>
          </div>

          {report.current_pregnancies.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Current Pregnancies</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doe</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buck</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Breeding Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expected Kidding</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Until</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.current_pregnancies.map((p, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{p.doe_tag}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{p.buck_tag}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(p.breeding_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(p.expected_kidding)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{p.days_until_kidding ?? 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={getStatusColor(p.status)}>{p.status.toUpperCase()}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.buck_performance.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Buck Performance</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Buck</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Successful</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kids Produced</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.buck_performance.map((buck, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{buck.buck_tag}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{buck.total_breedings}</td>
                        <td className="px-4 py-3 text-sm text-green-600">{buck.successful_breedings}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{buck.success_rate.toFixed(1)}%</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{buck.kids_produced}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.doe_performance.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Doe Performance</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Doe</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Successful</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success Rate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Kids</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Kids/Kidding</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.doe_performance.map((doe, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{doe.doe_tag}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{doe.total_breedings}</td>
                        <td className="px-4 py-3 text-sm text-green-600">{doe.successful_breedings}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{doe.success_rate.toFixed(1)}%</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{doe.total_kids_born}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{doe.average_kids_per_kidding?.toFixed(2) ?? 'N/A'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GoatBreedingPerformanceReportPage;

